import React, { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import Layout from '../../components/Layout/Layout';
import { MdSend, MdSmartToy } from '../../utils/icons';
import './BusinessAnalyst.css';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  time: string;
}

const formatTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const PLACEHOLDER_RESPONSES = [
  "I'm analysing your request. This feature will be connected to an AI backend soon.",
  "Great question! The AI agent will provide data-driven insights once the backend is wired up.",
  "Understood. I'll process that and return a full analysis shortly.",
];

const BusinessAnalyst: React.FC = () => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const initials = account?.name
    ? account.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 0,
      role: 'assistant',
      text: "Hello! I'm your OmniLinQ Business Analyst AI. Ask me anything about your data, reports, or access patterns.",
      time: formatTime(),
    },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const sendMessage = () => {
    const text = input.trim();
    if (!text || thinking) return;

    const userMsg: Message = { id: Date.now(), role: 'user', text, time: formatTime() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setThinking(true);

    setTimeout(() => {
      const reply = PLACEHOLDER_RESPONSES[Math.floor(Math.random() * PLACEHOLDER_RESPONSES.length)];
      const aiMsg: Message = { id: Date.now() + 1, role: 'assistant', text: reply, time: formatTime() };
      setMessages(prev => [...prev, aiMsg]);
      setThinking(false);
    }, 1200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Layout title="Business Analyst">
      <div className="chat-shell">

        {/* Header bar */}
        <div className="chat-topbar">
          <div className="chat-topbar-agent">
            <div className="chat-agent-avatar">
              <MdSmartToy aria-hidden="true" />
            </div>
            <div>
              <p className="chat-agent-name">OmniLinQ Business Analyst</p>
              <p className="chat-agent-status">
                <span className="chat-status-dot" />
                Online
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`chat-row ${msg.role === 'user' ? 'chat-row--user' : 'chat-row--ai'}`}
            >
              {msg.role === 'assistant' && (
                <div className="chat-bubble-avatar chat-bubble-avatar--ai">
                  <MdSmartToy aria-hidden="true" />
                </div>
              )}

              <div className="chat-bubble-wrap">
                <div className={`chat-bubble ${msg.role === 'user' ? 'chat-bubble--user' : 'chat-bubble--ai'}`}>
                  {msg.text}
                </div>
                <span className="chat-time">{msg.time}</span>
              </div>

              {msg.role === 'user' && (
                <div className="chat-bubble-avatar chat-bubble-avatar--user">
                  {initials}
                </div>
              )}
            </div>
          ))}

          {thinking && (
            <div className="chat-row chat-row--ai">
              <div className="chat-bubble-avatar chat-bubble-avatar--ai">
                <MdSmartToy aria-hidden="true" />
              </div>
              <div className="chat-bubble chat-bubble--ai chat-bubble--thinking">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chat-input-bar">
          <textarea
            className="chat-input"
            placeholder="Ask the Business Analyst AI…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className="chat-send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || thinking}
            aria-label="Send"
          >
            <MdSend aria-hidden="true" />
          </button>
        </div>

      </div>
    </Layout>
  );
};

export default BusinessAnalyst;
